import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getProcessDetail, type CanonicalProcessName } from '../processes.api';
import type { ProcessFilterValues } from '../procesLijsten';

function detailQueryKey(name: CanonicalProcessName | null, filters: ProcessFilterValues) {
  return ['processes', 'detail', name, filters] as const;
}

/**
 * Server-state for a single proces-detail page (funnel + doorlooptijd-KPI). `name: null`
 * (outside the canonical whitelist) never fires the query — no wasted fetch for a route the
 * page is about to reject anyway. Story 3.1: filters are part of the query key.
 */
export function useProcessDetail(name: CanonicalProcessName | null, filters: ProcessFilterValues = {}) {
  return useQuery({
    queryKey: detailQueryKey(name, filters),
    queryFn: () => getProcessDetail(name as CanonicalProcessName, filters),
    enabled: name != null,
    staleTime: 30 * 1000,
  });
}

/**
 * Story 4.2 (AD-22) — de StepTransitionTiming-blok's EIGEN ververs-affordance: roept
 * `getProcessDetail({ refreshEvents: true })` aan (invalideert uitsluitend de zware
 * `/events`-scan-cache server-side) en herlaadt daarna DEZELFDE detail-query-key. Dit is bewust
 * een APARTE hook van `useRefreshProcessesOverview` — de funnel-ververs (andere pagina, andere
 * query-key, geen `refreshEvents`-vlag) triggert deze zware scan nooit.
 */
export function useRefreshStepTiming(name: CanonicalProcessName | null, filters: ProcessFilterValues = {}) {
  const queryClient = useQueryClient();
  return async () => {
    if (name == null) return;
    await getProcessDetail(name, filters, { refreshEvents: true });
    await queryClient.invalidateQueries({ queryKey: detailQueryKey(name, filters) });
  };
}
